'use client';

import { AUTH_PROVIDER, SESSION_STATUS } from '@/constants/auth';
import { ENTITY_TAB } from '@/constants/entityTabs';
import { ROUTE_SEGMENT } from '@/constants/routeSegments';
import {
  createElement,
  type ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { signIn, useSession } from 'next-auth/react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import usePublicSearchContext from '@/publicSearch/store/usePublicSearchContext';
import { useIsI4Society } from '@/hooks/publicSearch/useIsI4Society';
import { useSearchType, SearchType } from '@/hooks/publicSearch/useSearchType';
import { buildSocietySearchHref } from '@/lib/publicSearchPaths';
import {
  I4_SEARCH_TYPES,
  SALE_CATALOGUE_CHILD_SEARCH_TYPES,
  SALE_ID_SEARCH_TYPES,
  SEARCH_TYPE,
} from '@/constants/searchType';
import { SEARCH_PARAM } from '@/constants/searchParams';
import { SOCIETY_SETTING_TYPE } from '@/constants/societySettings';
import useFilterStore from '@/publicSearch/store/useFilterStore';
import useGridStore from '@/publicSearch/store/useGridStore';
import useSearchStore from '@/publicSearch/store/useSearchStore';
import { useI4Links, useI4RedirectUrl } from '@/publicSearch/graphql/i4/hooks';
import type { UserI4LinkDto } from '@/publicSearch/graphql/i4/types';
import { PUBLIC_SEARCH_STATE_PARAM } from '@/lib/saleCataloguePaths';
import { cancelPendingAiSearch } from '@/hooks/publicSearch/aiSearchRequest';
import { logError } from '@/lib/logger';
type SocietySearchTab = {
  icon?: ReactNode;
  label: string;
  value: string;
};

const defaultTabs: SocietySearchTab[] = [
  { label: 'Animal Enquiry', value: SEARCH_TYPE.ANIMAL },
  { label: 'Member Enquiry', value: SEARCH_TYPE.MEMBERS },
  { label: 'Sale Catalogues', value: SEARCH_TYPE.SALE_CATALOGUES },
];

type TabConfig = {
  title: string;
  path: string;
  enabled: boolean;
  order: number;
};

type TabSetting = {
  value?: string | null;
  societySettingType?: string;
};

const settingTypeToTabValue: Record<string, string> = {
  ANIMAL_TAB: SEARCH_TYPE.ANIMAL,
  MEMBERS_TAB: SEARCH_TYPE.MEMBERS,
  EBV_TAB: ENTITY_TAB.EBVS,
  SALES_TAB: SEARCH_TYPE.SALE_CATALOGUES,
  MATING_TAB: SEARCH_TYPE.MATING,
  SEMEN_CATALOG_TAB: SEARCH_TYPE.SALE_CATALOGUES,
  DOWNLOADS_TAB: SEARCH_TYPE.DOWNLOADS,
  ONLINE_REGISTRATIONS_TAB: SEARCH_TYPE.ONLINE_REGISTRATIONS,
};

const TAB_JSON_SETTING_TYPES = new Set(Object.keys(settingTypeToTabValue));

function isLikelyTabConfigJson(value: string) {
  const t = value.trimStart();
  return t.startsWith('{') || t.startsWith('[');
}

const SOCIETY_SEARCH_PATH = new RegExp(
  `^/[^/]+/(${[
    ROUTE_SEGMENT.ANIMALS,
    ROUTE_SEGMENT.MEMBERS,
    ROUTE_SEGMENT.SALES,
    ROUTE_SEGMENT.EXTERNAL,
  ].join('|')})(/|$)`,
);

const pathToSettingType: Record<string, string> = {
  '/animal': 'ANIMAL_TAB',
  '/member': 'MEMBERS_TAB',
  '/ebvs': 'EBV_TAB',
  '/sales': 'SALES_TAB',
  '/mating': 'MATING_TAB',
  '/semen-catalog': 'SEMEN_CATALOG_TAB',
  '/downloads': 'DOWNLOADS_TAB',
  '/online-registrations': 'ONLINE_REGISTRATIONS_TAB',
};

export function useSocietySearchTabsModel() {
  const pathname = usePathname();
  const society = usePublicSearchContext((context) => context.society);
  const isI4Society = useIsI4Society();
  const { status } = useSession();
  const { clearFilter } = useFilterStore();
  const { paginationModel, setGridSortModel, setPaginationModel } =
    useGridStore();
  const { setSearchDisplayTerm, setIsAiMode, setAiSearchPrompt } =
    useSearchStore();
  const searchType = useSearchType();
  const router = useRouter();
  const searchParams = useSearchParams();
  const societyCode = society.societyCode ?? '';
  const isAuthenticated = status === SESSION_STATUS.AUTHENTICATED;
  const { data: linksData } = useI4Links(!isAuthenticated || !societyCode);
  const links = linksData?.i4Links ?? [];
  const linkForSociety = links.find(
    (l: UserI4LinkDto) => l.societyCode === societyCode,
  );
  const isLinked = !!linkForSociety;
  const { data: redirectUrlData } = useI4RedirectUrl(
    {
      societyCode,
      runMode: 'IO',
      societyId: linkForSociety?.societyId,
    },
    !isAuthenticated || !isLinked,
  );
  const i4RedirectUrl = redirectUrlData?.i4RedirectUrl;
  const [pendingTabReset, setPendingTabReset] = useState<{
    pathname: string;
    pageSize: number;
  } | null>(null);

  let tabValue = searchType;
  if (SALE_CATALOGUE_CHILD_SEARCH_TYPES.includes(searchType)) {
    tabValue = SEARCH_TYPE.SALE_CATALOGUES;
  }

  const tabs = useMemo(() => {
    if (!society.settings || society.loading) {
      return defaultTabs;
    }

    const tabSettings = society.settings.filter((setting) => {
      if (!setting.value) return false;
      if (setting.societySettingType === SOCIETY_SETTING_TYPE.SOCIETY_LOGO)
        return false;
      if (
        setting.societySettingType &&
        TAB_JSON_SETTING_TYPES.has(setting.societySettingType)
      ) {
        return true;
      }
      return (
        !setting.societySettingType && isLikelyTabConfigJson(setting.value)
      );
    }) as Array<TabSetting & { societySettingType?: string }>;

    if (tabSettings.length === 0) {
      return defaultTabs;
    }

    const processedTabs: Array<SocietySearchTab & { order: number }> = [];

    for (const setting of tabSettings) {
      try {
        const config: TabConfig = JSON.parse(setting.value!);
        if (!config.enabled) {
          continue;
        }
        let settingType = setting.societySettingType;
        if (!settingType && config.path) {
          settingType = pathToSettingType[config.path];
        }
        const mapped = settingType
          ? settingTypeToTabValue[settingType]
          : undefined;
        if (!mapped) {
          continue;
        }
        processedTabs.push({
          label: config.title || '',
          value: mapped,
          order: config.order ?? 999,
        });
      } catch (error) {
        logError(
          `Failed to parse tab setting for ${setting.societySettingType}:`,
          error,
        );
      }
    }

    processedTabs.sort((a, b) => a.order - b.order);
    const seen = new Set<string>();
    const deduped = processedTabs.filter((tab) => {
      if (seen.has(tab.value)) return false;
      seen.add(tab.value);
      return true;
    });

    const i4OnlyValues = new Set(I4_SEARCH_TYPES);
    let filtered = isI4Society
      ? deduped
      : deduped.filter((tab) => !i4OnlyValues.has(tab.value));

    const isEbvEnabled = society.isEbvEnabled ?? true;
    if (!isEbvEnabled) {
      filtered = filtered.filter(
        (tab) =>
          tab.value !== ENTITY_TAB.EBVS && tab.value !== SEARCH_TYPE.MATING,
      );
    }

    if (filtered.length === 0) {
      return defaultTabs;
    }

    return filtered.map(({ order: _order, ...tab }) => {
      if (!isAuthenticated && i4OnlyValues.has(tab.value)) {
        return {
          ...tab,
          icon: createElement(LockOutlinedIcon, { sx: { fontSize: 16 } }),
        };
      }
      return tab;
    });
  }, [
    society.settings,
    society.loading,
    society.isEbvEnabled,
    isI4Society,
    isAuthenticated,
  ]);

  const lockedTabs = useMemo(
    () => new Set(tabs.filter((t) => t.icon != null).map((t) => t.value)),
    [tabs],
  );

  function handleTabChange(value: string) {
    if (lockedTabs.has(value)) {
      signIn(AUTH_PROVIDER);
      return;
    }
    if (
      value === SEARCH_TYPE.ONLINE_REGISTRATIONS &&
      isAuthenticated &&
      isLinked &&
      i4RedirectUrl
    ) {
      window.open(i4RedirectUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    const orgCode = pathname?.split('/')[1] || society.societyCode || '';
    const nextParams = new URLSearchParams(searchParams?.toString() ?? '');
    nextParams.delete(SEARCH_PARAM.SEARCH_TYPE);
    nextParams.delete(PUBLIC_SEARCH_STATE_PARAM);
    nextParams.delete(SEARCH_PARAM.TAB);
    if (!SALE_ID_SEARCH_TYPES.includes(value)) {
      nextParams.delete(SEARCH_PARAM.SALE_ID);
      nextParams.delete(SEARCH_PARAM.LOT_NO);
    }
    const nextHref = buildSocietySearchHref(
      orgCode,
      value as SearchType,
      nextParams,
    );

    const nextPathname = nextHref.split('?')[0] || nextHref;

    setPendingTabReset({
      pathname: nextPathname,
      pageSize: paginationModel.pageSize,
    });
    cancelPendingAiSearch();
    router.push(nextHref);
  }

  useEffect(() => {
    if (!pendingTabReset || pathname !== pendingTabReset.pathname) return;

    clearFilter();
    setSearchDisplayTerm('');
    // Also exit AI mode + drop the prompt, else usePublicSearchUrlSync
    // re-serializes the old AI query into the URL and the tab isn't fresh.
    setIsAiMode(false);
    setAiSearchPrompt('');
    setGridSortModel([]);
    setPaginationModel({ page: 0, pageSize: pendingTabReset.pageSize });
    setPendingTabReset(null);
  }, [
    clearFilter,
    pendingTabReset,
    pathname,
    setAiSearchPrompt,
    setGridSortModel,
    setIsAiMode,
    setPaginationModel,
    setSearchDisplayTerm,
  ]);

  // Bounce the URL to the default tab if it points at a search type this
  // society doesn't expose. This must wait until the society's real tab config
  // has loaded — until then `tabs` falls back to `defaultTabs` (which omit
  // Mating/EBV), so running early would wrongly redirect a valid deep link
  // (e.g. `/animals/mating` opened in a new tab) to `/animals`, stripping the
  // path. Validate once, after the config is available.
  const hasValidatedSearchTypeRef = useRef<boolean>(false);
  useEffect(() => {
    if (hasValidatedSearchTypeRef.current) return;
    if (society.loading || !society.settings) return;
    hasValidatedSearchTypeRef.current = true;

    const validTabValues = tabs.map((tab) => tab.value) as SearchType[];
    const searchTypes: SearchType[] = [
      ...validTabValues,
      SEARCH_TYPE.SALE_CATALOGUES_DETAIL_ANIMALS,
      SEARCH_TYPE.SALE_CATALOGUES_NOTES,
      SEARCH_TYPE.SEMEN_CATALOGUES_DETAIL_ANIMALS,
      SEARCH_TYPE.DOWNLOADS,
      SEARCH_TYPE.ONLINE_REGISTRATIONS,
    ];
    if (searchTypes.includes(searchType)) return;
    const defaultTab = tabs.length > 0 ? tabs[0]!.value : SEARCH_TYPE.ANIMAL;
    handleTabChange(defaultTab);
    // Re-runs only when society readiness changes; the ref guards single
    // execution, so searchType/tabs/handleTabChange need not be dependencies.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [society.loading, society.settings]);

  const onSocietySearchRoute = SOCIETY_SEARCH_PATH.test(pathname);
  /** Only block the header on missing org context — not while settings load (avoids skeleton→tabs swap + layout jump). */
  const societyHeaderTabsLoading = onSocietySearchRoute && !society.orgId;

  return {
    tabs,
    tabValue,
    handleTabChange,
    pathname,
    society,
    societyHeaderTabsLoading,
  };
}
